import { Controller } from "@hotwired/stimulus"

// A native color swatch AND an editable hex text input, kept in sync and backed by a hidden text value
// so the color can be CLEARED back to blank ("use default") — something <input type="color"> alone
// can't represent (it always resolves to a color). Picking the swatch or typing a valid #rrggbb writes
// the value; emptying the hex clears it.
export default class extends Controller {
  static targets = ["picker", "value", "text"]
  static values = { default: String }

  // Swatch picked → mirror into the hidden value + the hex text.
  pick() {
    const color = this.pickerTarget.value
    this.valueTarget.value = color
    this.textTarget.value = color.toUpperCase()
    this.markDefault(false)
  }

  // Hex typed → normalize (tolerate a missing "#"). A valid #rrggbb drives the swatch + value; an empty
  // entry clears to default; a partial/invalid entry is left alone until it becomes valid, so mid-typing
  // doesn't fight the user.
  typeHex() {
    const raw = this.textTarget.value.trim()
    const hex = raw && !raw.startsWith("#") ? `#${raw}` : raw

    if (hex === "") {
      this.resetToDefault()
    } else if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
      this.valueTarget.value = hex
      this.pickerTarget.value = hex
      this.markDefault(false)
    }
  }

  // On commit (blur / Enter), snap the text back to the committed value so a half-typed or invalid entry
  // never lingers in the field.
  commitHex() {
    const value = this.valueTarget.value
    this.textTarget.value = value ? value.toUpperCase() : ""
  }

  clear() {
    this.resetToDefault()
    this.textTarget.value = ""
    // Clearing changes the committed value programmatically (no native event fires), so emit a bubbling
    // `change` — lets a live preview refresh off the same `change->` action a real pick/type uses.
    this.pickerTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Blank the committed value ("use default"): the swatch shows the default colour but the hidden field
  // stays empty so nothing is submitted.
  resetToDefault() {
    this.valueTarget.value = ""
    this.pickerTarget.value = this.defaultValue || "#000000"
    this.markDefault(true)
  }

  markDefault(isDefault) {
    this.element.classList.toggle("color-field--default", isDefault)
  }
}
